import { Box, styled } from "@mui/material"
import { type ElementType, type ReactNode, isValidElement, useMemo } from "react"
import { ArrowRight, TrendingDown, TrendingUp } from "react-feather"
import { isDefined } from "../../.."
import {
  type FormatFunction,
  formatNumber,
  formatPercentage,
  noDecimalsFormatOptions,
} from "../numberDisplays"

export interface EvolutionStatChipProps {
  evol?: number | null
  formatOptions?: Intl.NumberFormatOptions
  noValueLabel?: string
  arrow?: boolean
  border?: boolean
  isNeutral?: boolean
  bold?: boolean
  startLabel?: ReactNode
  endLabel?: ReactNode
}

export const EvolutionStatChip = (props: EvolutionStatChipProps) => (
  <EvolutionChip {...props} format={formatPercentage} />
)

export const EvolutionUnitStatChip = (props: EvolutionStatChipProps) => (
  <EvolutionChip {...props} format={formatNumber} />
)

interface EvolutionChipProps extends EvolutionStatChipProps {
  format: FormatFunction
}

const EvolutionChip = ({
  evol,
  noValueLabel = "N/A",
  arrow = false,
  border = true,
  bold = false,
  format,
  startLabel,
  endLabel,
  ...config
}: EvolutionChipProps) => {
  const value = config.formatOptions ? evol : evol ? Math.round(evol) : evol
  const { formatOptions, icon, ...pillProps } = useConfig({
    evol: value,
    arrow,
    bold,
    ...config,
  })

  return (
    <Pill
      {...pillProps}
      border={border}
      icon={isDefined(value) ? icon : null}
      formattedValue={isDefined(value) ? format(value, formatOptions) : noValueLabel}
      startLabel={startLabel}
      endLabel={endLabel}
    />
  )
}

const useConfig = ({
  evol,
  arrow,
  bold,
  isNeutral,
  formatOptions,
}: EvolutionStatChipProps) =>
  useMemo(() => {
    const evolutionConf = getEvolutionConfig({ value: evol })

    return {
      fontWeight: bold ? 500 : 400,
      color: isNeutral ? "grey.light" : evolutionConf.color,
      icon: arrow ? evolutionConf.icon(bold ? 3 : 2) : null,
      formatOptions: {
        signDisplay: "exceptZero" as Intl.NumberFormatOptions["signDisplay"],
        ...noDecimalsFormatOptions,
        ...(formatOptions || {}),
      },
    }
  }, [evol, arrow, bold, isNeutral, formatOptions])

interface PillProps {
  formattedValue: string
  color: string
  fontWeight: number
  border: boolean
  icon?: ReactNode
  startLabel?: ReactNode
  endLabel?: ReactNode
}

const Pill = ({
  formattedValue,
  icon,
  startLabel,
  endLabel,
  border,
  ...pillProps
}: PillProps) => (
  <PillStyled component="span" withBorder={border} {...pillProps}>
    {showLabel(startLabel)}
    {icon}
    {formattedValue}
    {showLabel(endLabel)}
  </PillStyled>
)

const showLabel = (label?: ReactNode) =>
  isDefined(label) &&
  (isValidElement(label) ? label : <PillLabel>{label}</PillLabel>)

const getEvolutionConfig = ({ value }: { value?: number | null }) => {
  const status =
    value != null
      ? value > 0
        ? "positive"
        : value < 0
          ? "negative"
          : "neutral"
      : "neutral"

  return valueConfigEnum[status]
}

const valueConfigEnum = {
  positive: {
    color: "success.main",
    icon: (width: number) => <TrendingUp size="1.1em" strokeWidth={width} />,
  },
  negative: {
    color: "error.main",
    icon: (width: number) => <TrendingDown size="1.1em" strokeWidth={width} />,
  },
  neutral: {
    color: "grey.light",
    icon: (width: number) => <ArrowRight size="1.1em" strokeWidth={width} />,
  },
}

const PillLabel = styled("span")({})

const PillStyled = styled(Box, {
  shouldForwardProp: (prop) => prop !== "withBorder",
})<{ withBorder: boolean; component?: ElementType }>(({ theme, withBorder }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(0.5),
  borderColor: "currentColor",
  borderStyle: withBorder ? "solid" : "none",
  borderWidth: withBorder ? 1 : 0,
  borderRadius: "100px",
  width: "fit-content",
  height: "fit-content",
  paddingInline: withBorder ? theme.spacing(1) : 0,
}))
